import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LayoutDashboard, Sun, Moon, User, LogOut, Ticket, Languages, ArrowLeft } from 'lucide-react';
import starlineLogo from '@/assets/starline-logo.webp';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from 'next-themes';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { staffLandingPath } from '@/lib/adminAccess';

const navLinks = [
  { to: '/', key: 'nav_home' },
  { to: '/search', key: 'nav_search' },
  { to: '/routes', key: 'nav_routes' },
  { to: '/counters', key: 'nav_counters' },
  { to: '/notices', key: 'nav_notices' },
  { to: '/manage-booking', key: 'nav_manage' },
  { to: '/live-tracking', key: 'nav_tracking' },
  { to: '/support', key: 'nav_support' },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { user, profile, signOut } = useAuth();
  const { lang, setLang, t } = useLanguage();
  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');
  const toggleLang = () => setLang(lang === 'en' ? 'bn' : 'en');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const staffLanding = staffLandingPath(profile?.role);

  // Close dropdown on outside click
  useEffect(() => {
    if (!userMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [userMenuOpen]);

  const handleSignOut = async () => {
    await signOut();
    setUserMenuOpen(false);
    navigate('/');
  };

  return (
    <nav className="relative z-50 bg-background/70 backdrop-blur-2xl border-b border-border/30">
      <div className="container flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2">
          <img src={starlineLogo} alt="Star Line Group" className="h-10 w-auto" />
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-0.5">
          {navLinks.map(l => (
            <Link
              key={l.to}
              to={l.to}
              className={`px-3.5 py-2 rounded-lg text-[13px] font-medium transition-colors ${
                location.pathname === l.to
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
              }`}
            >
              {t(l.key)}
            </Link>
          ))}
          <div className="w-px h-5 bg-border mx-2" />
          {/* Signed-out visitors need the language switch here — they have no
              profile to set it in. Once signed in it lives in My Profile, so
              there is one place to change it rather than two that can disagree. */}
          {!user && (
            <button
              onClick={toggleLang}
              className="px-2.5 py-1.5 rounded-lg text-xs font-bold text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors border border-border/40"
              aria-label={t('toggle_language')}
              title={lang === 'en' ? 'Switch to Bangla' : 'Switch to English'}
            >
              {lang === 'en' ? 'বাংলা' : 'EN'}
            </button>
          )}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
            aria-label={t('toggle_theme')}
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          {user ? (
            staffLanding ? (
              /* ── Staff user on public site: slim "back" link ── */
              <Link
                to={staffLanding}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[13px] font-medium text-primary hover:bg-primary/10 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back to control room
              </Link>
            ) : (
            /* Logged in — user dropdown */
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-secondary/60 transition-colors"
                aria-label={t('account_menu')}
                aria-expanded={userMenuOpen}
                aria-haspopup="menu"
              >
                <div className="w-7 h-7 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center overflow-hidden">
                  {(profile?.avatar_url || user.user_metadata?.avatar_url || user.user_metadata?.picture) ? (
                    <img src={profile?.avatar_url || user.user_metadata?.avatar_url || user.user_metadata?.picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-3.5 h-3.5 text-primary" />
                  )}
                </div>
                <span className="text-xs font-medium text-foreground max-w-[100px] truncate">
                  {profile?.full_name || user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0]}
                </span>
              </button>

              <AnimatePresence>
                {userMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="absolute right-0 top-full mt-1 w-52 bg-background border border-border rounded-xl shadow-xl overflow-hidden"
                  >
                    <div className="p-3 border-b border-border">
                      <p className="text-sm font-medium text-foreground truncate">{profile?.full_name || user.user_metadata?.full_name || user.user_metadata?.name || 'User'}</p>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    </div>
                    <div className="p-1">
                        {/* Passenger menu items */}
                          <Link
                            to="/profile"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-secondary/60 rounded-lg transition-colors"
                          >
                            <User className="w-4 h-4 text-muted-foreground" /> {t('nav_profile')}
                          </Link>
                          <Link
                            to="/dashboard"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-secondary/60 rounded-lg transition-colors"
                          >
                            <LayoutDashboard className="w-4 h-4 text-muted-foreground" /> {t('nav_dashboard')}
                          </Link>
                          <Link
                            to="/manage-booking"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-secondary/60 rounded-lg transition-colors"
                          >
                            <Ticket className="w-4 h-4 text-muted-foreground" /> {t('nav_my_bookings')}
                          </Link>
                    </div>
                    <div className="p-1 border-t border-border">
                      <button
                        onClick={handleSignOut}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 rounded-lg transition-colors w-full"
                      >
                        <LogOut className="w-4 h-4" /> {t('nav_logout')}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            )
          ) : (
            /* Not logged in — Login button */
            <Link
              to="/signin"
              className="px-4 py-2 rounded-lg text-[13px] font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {t('nav_login')}
            </Link>
          )}
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen(!open)}
          className="md:hidden p-3 min-w-[44px] min-h-[44px] flex items-center justify-center text-foreground rounded-lg hover:bg-secondary/60"
          aria-label={open ? t('close_menu') : t('open_menu')}
          aria-expanded={open}
          aria-controls="mobile-nav"
        >
          {open ? <X className="w-5 h-5" aria-hidden="true" /> : <Menu className="w-5 h-5" aria-hidden="true" />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            id="mobile-nav"
            className="md:hidden absolute top-full left-0 right-0 bg-background/95 backdrop-blur-2xl border-b border-border overflow-hidden z-50"
          >
            <div className="container py-3 flex flex-col gap-0.5">
              {navLinks.map(l => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === l.to
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
                  }`}
                >
                  {t(l.key)}
                </Link>
              ))}
              {/* Same rule as the desktop rail: signed in, language lives in
                  My Profile. */}
              {!user && (
                <button
                  onClick={toggleLang}
                  className="px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors flex items-center gap-2"
                >
                  <Languages className="w-4 h-4" />
                  {lang === 'en' ? 'বাংলায় দেখুন' : 'Switch to English'}
                </button>
              )}
              <button
                onClick={toggleTheme}
                className="px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors flex items-center gap-2"
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </button>
              {user ? (
                <>
                  {staffLanding ? (
                    /* ── Staff user on public site — prominent back link ── */
                    <Link to={staffLanding} onClick={() => setOpen(false)} className="px-4 py-3 rounded-lg text-sm font-semibold text-primary bg-primary/10 flex items-center gap-2">
                      <ArrowLeft className="w-4 h-4" /> Back to control room
                    </Link>
                  ) : (
                    /* ── Passenger mobile menu items ── */
                    <>
                      <Link to="/profile" onClick={() => setOpen(false)} className="px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/60 flex items-center gap-2">
                        <User className="w-4 h-4" /> {t('nav_profile')}
                      </Link>
                      <Link to="/dashboard" onClick={() => setOpen(false)} className="px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/60 flex items-center gap-2">
                        <LayoutDashboard className="w-4 h-4" /> {t('nav_dashboard')}
                      </Link>
                    </>
                  )}
                  <button onClick={handleSignOut} className="px-4 py-3 rounded-lg text-sm font-medium text-destructive hover:bg-destructive/10 flex items-center gap-2">
                    <LogOut className="w-4 h-4" /> {t('nav_logout')}
                  </button>
                </>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="px-4 py-3 rounded-lg text-sm font-semibold bg-primary text-primary-foreground text-center">
                  {t('nav_login')}
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
